import { Show, useContext } from 'solid-js';
import { Link } from '@solidjs/router';
import { OrderLineQuantity, RemoveButton } from './basket-components';
import { MappedOrderDetails } from './basket';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ImageComponent } from '../image-component/image-component';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import decodeHtmlEntities from '../../tools/decode-html-entities';

type BasketOrderLineProps = {
    orderDetails: MappedOrderDetails;
};

/* One basket row on the flat list recipe used across the rebrand (events list, cards):
   thumbnail in a neutral rounded box, copy beside it, quantity stepper + remove on the
   right, hairline between rows. Controls drop under the copy below the tablet-original
   breakpoint. */
const BasketOrderLine = ({ orderDetails }: BasketOrderLineProps) => {
    const { localize } = useContext(AppContext);

    const unitsPerPack = () => orderDetails.sku?.quantity
        ? `${localize('units-per-pack', 'Units per pack')}: ${orderDetails.sku.quantity}`
        : '';

    return (
        <li class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-cvi-6 gap-y-cvi-5 border-b border-cvi-border-default py-cvi-6 tablet-original:grid-cols-[auto_minmax(0,1fr)_auto] tablet-original:gap-x-cvi-8">
            <div class="flex size-cvi-card-product-image shrink-0 items-center justify-center overflow-hidden rounded-cvi-md bg-cvi-bg-neutral-subtle">
                <Show when={orderDetails.imageUrl}>
                    <ImageComponent
                        src={orderDetails.imageUrl!}
                        height={'AUTO'}
                        width={300}
                        altText={'Product image for ' + orderDetails.sku.name}
                        aspectRatio={'original'}
                        ratio={orderDetails.imageRatio}
                        roundCorners={false}
                        spacer={false}
                    />
                </Show>
            </div>

            <div class="flex min-w-0 flex-col gap-cvi-3">
                <Link
                    href={removeTrailingSlash(orderDetails.product.url)}
                    class="heading-md font-cvi-medium text-cvi-fg-default no-underline transition-colors duration-200 hover:text-cvi-fg-link"
                >
                    {decodeHtmlEntities(orderDetails.sku.name ?? '')}
                </Link>
                <Show when={orderDetails.product.shortDescription}>
                    <p class="m-0 body-sm font-cvi-regular text-cvi-fg-default line-clamp-2">
                        {decodeHtmlEntities(orderDetails.product.shortDescription ?? '')}
                    </p>
                </Show>
                <div class="flex flex-col gap-cvi-1 label-sm font-cvi-regular text-cvi-fg-muted">
                    <Show when={orderDetails.sku.description}>
                        <span class="line-clamp-2">{decodeHtmlEntities(orderDetails.sku.description ?? '')}</span>
                    </Show>
                    <Show when={unitsPerPack()}>
                        <span>{unitsPerPack()}</span>
                    </Show>
                </div>
            </div>

            <div class="col-start-2 flex flex-wrap items-center gap-cvi-4 tablet-original:col-start-3 tablet-original:row-start-1 tablet-original:flex-nowrap">
                <OrderLineQuantity orderDetails={orderDetails} />
                <RemoveButton orderDetails={orderDetails} />
            </div>
        </li>
    );
};

export default BasketOrderLine;
